import { createUseStyles, Styles } from '@pff-consumer/tweed-ui/web-styles'

interface PromoCodeStyles {
  container: Styles
  inactiveContainer: Styles
  promoCodeContainer: Styles
  promoCodeButton: Styles
  promoCodeButtonActive: Styles
  innerContainer: Styles
  innerContainerActive: Styles
  promoCodeTextFieldContainer: Styles
  promoCodeTextField: Styles
  promoCodeTextFieldError: Styles
  promoCodeTextFieldClear: Styles
  promoCodeApplyButton: Styles
  promoCodeApplied: Styles
  errorContainer: Styles
  promoCodeApplicabilityContainer: Styles
  verifyButton: Styles
}

interface PromoCodeStylesProps {
  isIdme: boolean
}

const INNER_CONTAINER_GAP = 10
const CLEAR_BUTTON_VERTICAL_PADDING = 7
const CLEAR_BUTTON_PADDING_RIGHT = 7
const APPLY_BUTTON_PADDING = 7
const PROMO_CODE_TEXT_FIELD_PADDING = 10
const PROMO_CODE_TEXT_FIELD_BORDER_WIDTH = 1
const PROMO_CODE_TEXT_FIELD_FONT_SIZE = 16

export const useStyles = createUseStyles<PromoCodeStyles, PromoCodeStylesProps>((theme) => {
  return {
    container: {
      marginTop: theme.calcUnit(theme.spacing.s3),
      display: 'flex',
      flexDirection: 'column',
    },
    inactiveContainer: {
      marginBottom: theme.calcUnit(-theme.spacing.s3),
    },
    promoCodeContainer: {
      display: 'flex',
    },
    promoCodeButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `${theme.calcUnit(theme.spacing.s3)} ${theme.calcUnit(theme.spacing.s3)}`,
      borderRadius: theme.calcUnit(theme.borderRadius.roundedDefault),
      borderBottomLeftRadius: theme.calcUnit(theme.borderRadius.roundedDefault),
      borderBottomRightRadius: theme.calcUnit(theme.borderRadius.roundedDefault),
      width: '100%',
      cursor: 'pointer',
      color: theme.colors.webLinkText,
      backgroundColor: theme.colors.webFadedBackground,
    },
    promoCodeButtonActive: {
      borderBottomLeftRadius: theme.calcUnit(0),
      borderBottomRightRadius: theme.calcUnit(0),
    },
    innerContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.calcUnit(INNER_CONTAINER_GAP),
      backgroundColor: theme.colors.webFadedBackground,
      borderBottomLeftRadius: theme.calcUnit(theme.borderRadius.roundedDefault),
      borderBottomRightRadius: theme.calcUnit(theme.borderRadius.roundedDefault),
      padding: `${theme.calcUnit(0)} ${theme.calcUnit(theme.spacing.s3)} ${theme.calcUnit(theme.spacing.s3)} ${theme.calcUnit(theme.spacing.s3)}`,
      opacity: theme.opacity.transparent,
      height: 0,
      transition: 'opacity 0.3s ease-out',
      visibility: 'hidden',
    },
    innerContainerActive: {
      opacity: theme.opacity.opaque,
      height: 'auto',
      transition: 'opacity 0.3s ease-in',
      visibility: 'visible',
    },
    promoCodeTextFieldContainer: {
      position: 'relative',
      display: 'flex',
      flex: 1,
    },
    promoCodeTextField: ({ isIdme }) => ({
      // input placeholder font style
      fontFamily: 'Archivo',
      color: isIdme ? theme.colors.subscriptionCouponTextColor : theme.colors.webTextColorDefault,
      fontSize: theme.calcUnit(PROMO_CODE_TEXT_FIELD_FONT_SIZE),
      width: '100%',
      borderTop: `${theme.calcUnit(PROMO_CODE_TEXT_FIELD_BORDER_WIDTH)} solid ${theme.colors.webTextFieldBorder}`,
      borderBottom: `${theme.calcUnit(PROMO_CODE_TEXT_FIELD_BORDER_WIDTH)} solid ${theme.colors.webTextFieldBorder}`,
      borderLeft: `${theme.calcUnit(PROMO_CODE_TEXT_FIELD_BORDER_WIDTH)} solid ${theme.colors.webTextFieldBorder}`,
      borderTopLeftRadius: theme.calcUnit(theme.borderRadius.roundedSm),
      borderBottomLeftRadius: theme.calcUnit(theme.borderRadius.roundedSm),
      borderTopRightRadius: theme.calcUnit(0),
      borderBottomRightRadius: theme.calcUnit(0),
      paddingBlock: theme.calcUnit(PROMO_CODE_TEXT_FIELD_PADDING),
      paddingLeft: theme.calcUnit(PROMO_CODE_TEXT_FIELD_PADDING),
      paddingRight: theme.calcUnit(0),
      '&:focus': {
        outline: 'none',
      },
    }),
    promoCodeTextFieldError: {
      borderColor: theme.colors.webErrorColor,
    },
    promoCodeTextFieldClear: {
      position: 'absolute',
      right: theme.calcUnit(0),
      top: '50%',
      transform: 'translate(0, -50%)',
      cursor: 'pointer',
      padding: `${theme.calcUnit(CLEAR_BUTTON_VERTICAL_PADDING)} ${theme.calcUnit(CLEAR_BUTTON_PADDING_RIGHT)} ${theme.calcUnit(CLEAR_BUTTON_VERTICAL_PADDING)} ${theme.calcUnit(theme.spacing.s4)}`,
      background: `linear-gradient(90deg, ${theme.colors.webBrightBackground}66 0%, ${theme.colors.webBrightBackground}e6 25%, ${theme.colors.webBrightBackground} 100%)`,
    },
    promoCodeApplyButton: {
      backgroundColor: theme.colors.couponAppliedBackgroundColor,
      color: theme.colors.webInverseTextColor,
      padding: theme.calcUnit(APPLY_BUTTON_PADDING),
      borderTopRightRadius: theme.calcUnit(theme.borderRadius.roundedSm),
      borderBottomRightRadius: theme.calcUnit(theme.borderRadius.roundedSm),
      cursor: 'pointer',
    },
    promoCodeApplied: {
      opacity: theme.opacity.semiTransparent,
      cursor: 'default',
    },
    errorContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.calcUnit(theme.spacing.s1),
    },
    promoCodeApplicabilityContainer: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    verifyButton: ({ isIdme }) => ({
      cursor: 'pointer',
      opacity: isIdme ? theme.opacity.semiTransparent : theme.opacity.opaque,
      pointerEvents: isIdme ? 'none' : 'all',
      marginRight: theme.calcUnit(-theme.spacing.s1),
    }),
  }
})
